import {
  Toggle,
  styled,
  css,
  LockIcon,
  UnlockIcon,
  Label,
  Stack,
  Paragraph,
  Inline,
  Card,
} from 'canoe';
import {
  ENDPOINTS,
  createCacheKey,
  fetchResource,
  handleErrorResponse,
  useActions,
  useCreatePayload,
} from 'hammock';
import React from 'react';
import { useQuery } from 'react-query';

import { useNotification } from '@candu-ai/users/notification';

const LockWrapper = styled.div<{ isLocked: boolean }>`
  ${({ isLocked, theme }) => css`
    align-items: center;
    background-color: ${isLocked ? theme.colors.White : theme.colors.AquaTealL30};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    height: 40px;
    width: 40px;
    margin-right: ${theme.spaces.xs};
  `}
`;

const ToggleContainer = styled.div`
  align-self: center;
  display: flex;
`;

type Permission = {
  id: string;
  permittedUsers: string[];
};
interface LockContentProps {
  isOwner: boolean;
  isPublisher: boolean;
  customerId: string;
  contentId: string;
  entityPermissionId?: number;
}
export const LockContent = ({
  isOwner,
  isPublisher,
  customerId,
  contentId,
  entityPermissionId,
}: LockContentProps) => {
  const notificationContext = useNotification();
  const { get } = useCreatePayload(ENDPOINTS.entityPermission);
  const entityPermissionActions = useActions(ENDPOINTS.entityPermission);
  const getPayload = get({ id: `${entityPermissionId}` });

  const { data: entityPermissions, isLoading } = useQuery(createCacheKey(getPayload), () =>
    !entityPermissionId
      ? null
      : fetchResource<Permission | null>(getPayload).catch((e) =>
          handleErrorResponse(e, getPayload, notificationContext),
        ),
  );

  const isLocked = entityPermissions !== null;

  const isPermittedUser = (permissions: Permission, userId: string) => {
    if (permissions === null) {
      return false;
    }
    return permissions?.permittedUsers?.includes(userId);
  };

  const getLockableMessage = (
    permissions: Permission | null | undefined,
    hasRole: boolean,
    userId: string,
  ) => {
    if (permissions) {
      if (isPermittedUser(permissions, userId) || hasRole) {
        return 'Only you or an organization owner can edit this Content.';
      }
      return 'You do not have permissions to modify this Content.';
    }
    return 'Any team member can modify this Content.';
  };

  const isDisabled = entityPermissions
    ? isOwner || isPermittedUser(entityPermissions, customerId)
    : isPublisher;

  const toggleLock = () => {
    if (entityPermissions) {
      entityPermissionActions.delete({
        id: entityPermissions.id,
        invalidates: {
          [ENDPOINTS.contentMetadata]: { id: contentId },
        },
      });
    } else {
      entityPermissionActions.create({
        body: {
          permittedUsers: [],
          resourceType: 'Content',
          requiredRole: 'Owner',
          resourceId: contentId,
        },
        invalidates: {
          [ENDPOINTS.contentMetadata]: { id: contentId },
        },
      });
    }
  };
  if (isLoading) {
    return null;
  }
  return (
    <Card padding="s" elevation="+1">
      <Inline space="none" justify="space-between" align="center">
        <Inline space="none" align="center">
          <LockWrapper isLocked={isLocked}>
            {isLocked ? (
              <LockIcon size="l" color="OnyxGrayL10" />
            ) : (
              <UnlockIcon size="l" color="AquaTealL10" />
            )}
          </LockWrapper>
          <Stack spaceAfter="none" space="xs">
            <Label> This content is {isLocked ? 'locked' : 'unlocked'}</Label>
            <Paragraph color="neutral">
              {getLockableMessage(entityPermissions, isOwner, customerId)}
            </Paragraph>
          </Stack>
        </Inline>
        <ToggleContainer>
          <Toggle
            variant="primary"
            toggled={isLocked}
            onToggle={toggleLock}
            disabled={isDisabled}
          />
        </ToggleContainer>
      </Inline>
    </Card>
  );
};
